import { ApolloClient, InMemoryCache, HttpLink, ApolloLink } from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { CombinedGraphQLErrors, CombinedProtocolErrors } from "@apollo/client/errors";
import { SetContextLink } from "@apollo/client/link/context";
import { GetUserAccessTokenUseCase } from "@/core/usecases/user";
import { UserRepository } from "@/infrastructure/repositories/user";
import { AuthRepository } from "@/infrastructure/repositories/auth";
import { GetWorkSpaceAccessTokenUseCase, GetWorkSpaceRefreshTokenUseCase } from "@/core/usecases/workspace";
import { WorkSpaceRepository } from "@/infrastructure/repositories/workspace";
import { AppConfig } from "@/infrastructure/services/config";
import { graphQlInterceptorLink } from "./graphQl.interceptorLink";
import { graphQlVariablesLink } from "./graphQlVariablesLink";
import { GetUserDetailsUseCase } from "@/core/usecases/auth";
import { createLogger, ILogger } from "@/infrastructure/services/logger";

const _appConfig = AppConfig.getInstance();
const GRAPHQL_BASE_URL = _appConfig.GRAPHQL_BASE_URL;

export class GraphQlAdapter {
  private readonly client: ApolloClient;
  private static instance: GraphQlAdapter;
  private readonly logger: ILogger = createLogger("GraphQlAdapter");

  private constructor() {
    this.client = this.initializeClient();
  }

  public static getInstance(): GraphQlAdapter {
    if (!GraphQlAdapter.instance) {
      GraphQlAdapter.instance = new GraphQlAdapter();
    }
    return GraphQlAdapter.instance;
  }

  private async getAuthTokens() {
    const authRepository = new AuthRepository();
    const userRepository = new UserRepository(authRepository);
    const workspaceRepository = new WorkSpaceRepository(authRepository);

    const userAccessToken = await new GetUserAccessTokenUseCase(userRepository).execute();
    let brandAccessToken = await new GetWorkSpaceAccessTokenUseCase(workspaceRepository).execute();
    let brandRefreshToken = await new GetWorkSpaceRefreshTokenUseCase(workspaceRepository).execute();

    if (!brandAccessToken || !brandRefreshToken) {
      const userDetails = await new GetUserDetailsUseCase(new AuthRepository()).execute();
      if (userDetails.type === "success") {
        brandAccessToken = await new GetWorkSpaceAccessTokenUseCase(workspaceRepository).execute();
        brandRefreshToken = await new GetWorkSpaceRefreshTokenUseCase(workspaceRepository).execute();
      }
    }

    return { userAccessToken, brandAccessToken, brandRefreshToken };
  }

  private initializeClient(): ApolloClient {
    let userAccessToken: string | null = null;
    let brandAccessToken: string | null = null;
    let brandRefreshToken: string | null = null;
    const httpLink = new HttpLink({
      uri: GRAPHQL_BASE_URL,
    });

    const errorLink = new ErrorLink(({ error }) => {
      if (CombinedGraphQLErrors.is(error)) {
        for (const { message, locations, path } of error.errors) {
          this.logger.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
        }
      } else if (CombinedProtocolErrors.is(error)) {
        for (const { message, extensions } of error.errors) {
          this.logger.error(`[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`);
        }
      } else {
        this.logger.error(`[Network error]: ${error.message}`);
      }
    });

    const authLink = new SetContextLink(async prevContext => {
      const tokens = await this.getAuthTokens();
      userAccessToken = tokens.userAccessToken;
      brandAccessToken = tokens.brandAccessToken;
      brandRefreshToken = tokens.brandRefreshToken;

      return {
        ...prevContext,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: userAccessToken ? `Bearer ${userAccessToken}` : undefined,
          "X-Brand-Authorization": brandAccessToken ? `Bearer ${brandAccessToken}` : undefined,
          "X-Brand-Refresh-Token": brandRefreshToken ? `Bearer ${brandRefreshToken}` : undefined,
        },
      };
    });

    return new ApolloClient({
      link: ApolloLink.from([errorLink, authLink, graphQlInterceptorLink, graphQlVariablesLink, httpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          errorPolicy: "all",
        },
        query: {
          errorPolicy: "all",
        },
        mutate: {
          errorPolicy: "all",
        },
      },
    });
  }

  getClient(): ApolloClient {
    return this.client;
  }
}
